import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CoPresentRounded, ArrowBackRounded, CloseRounded, AddRounded } from '@mui/icons-material';
import { StudentOptions, TeacherClassItem, TeacherSubject } from '../types';
import { getStudentOptions, loginTeacher, registerTeacher } from '../services/api';
import { useTeacherAuth } from '../context/TeacherAuthContext';
import SubjectChipsSelect from './common/SubjectChipsSelect';
import PasswordInput from './common/PasswordInput';

const TeacherAuth: React.FC = () => {
  const navigate = useNavigate();
  const { loginTeacher: setTeacherSession } = useTeacherAuth();

  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [school, setSchool] = useState('');
  const [classes, setClasses] = useState<TeacherClassItem[]>([]);
  const [subjects, setSubjects] = useState<TeacherSubject[]>([]);
  const [substituteSubjects, setSubstituteSubjects] = useState<TeacherSubject[]>([]);
  const [pendingGrade, setPendingGrade] = useState('');
  const [pendingSection, setPendingSection] = useState('');

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [options, setOptions] = useState<StudentOptions | null>(null);

  useEffect(() => {
    let mounted = true;
    getStudentOptions()
      .then((o) => mounted && setOptions(o))
      .catch(() => mounted && setError('Failed to load school options. Please refresh.'));
    return () => {
      mounted = false;
    };
  }, []);

  const fieldClass =
    'appearance-none relative block w-full rounded-xl border border-violet-200 bg-white px-3 py-3 text-base text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 sm:py-2 sm:text-sm disabled:cursor-not-allowed disabled:bg-violet-50';
  const labelClass = 'mb-1 block text-sm font-medium text-slate-700';

  const switchMode = (mode: 'login' | 'register') => {
    setAuthMode(mode);
    setError('');
  };

  const addClass = () => {
    if (!pendingGrade) {
      setError('Pick a grade to add a class.');
      return;
    }
    const grade = Number(pendingGrade);
    const section = pendingSection.trim().toUpperCase() || null;
    const exists = classes.some((c) => c.grade === grade && (c.section || null) === section);
    if (exists) {
      setError('That class is already added.');
      return;
    }
    setClasses([...classes, { grade, section }]);
    setPendingGrade('');
    setPendingSection('');
    setError('');
  };

  const removeClass = (idx: number) => {
    setClasses(classes.filter((_, i) => i !== idx));
  };

  const classLabel = (c: TeacherClassItem) =>
    `Grade ${c.grade}${c.section ? ` · Section ${c.section}` : ''}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password) {
      setError('Please enter a username and password.');
      return;
    }

    if (authMode === 'register') {
      if (!school) {
        setError('Please select your school.');
        return;
      }
      if (subjects.length === 0) {
        setError('Pick at least one subject you teach.');
        return;
      }
      if (classes.length === 0) {
        setError('Add at least one class you teach.');
        return;
      }
    }

    setIsLoading(true);
    try {
      const response =
        authMode === 'register'
          ? await registerTeacher({
              username: username.trim(),
              password,
              name: name.trim() || undefined,
              school,
              classes,
              subjects,
              substitute_subjects: substituteSubjects,
            })
          : await loginTeacher(username.trim(), password);

      if (response.success && response.token && response.refresh_token && response.teacher) {
        setTeacherSession(response.token, response.refresh_token, response.teacher);
        navigate('/teacher-view');
      } else {
        setError(response.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to continue.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="main-gradient-bg min-h-screen flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
      <div className="w-full max-w-md space-y-6 rounded-3xl border border-violet-200 bg-white/95 p-6 shadow-sm sm:p-8">
        <div className="text-center">
          <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700">
            <CoPresentRounded />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900">Teacher access</h2>
          <p className="mt-2 text-sm text-violet-700">
            {authMode === 'register'
              ? 'Create an account and pick the classes you teach.'
              : 'Log in to view your class reports.'}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-1 rounded-xl bg-violet-50 p-1">
          <button
            type="button"
            onClick={() => switchMode('login')}
            className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              authMode === 'login' ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-violet-700'
            }`}
          >
            Log in
          </button>
          <button
            type="button"
            onClick={() => switchMode('register')}
            className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              authMode === 'register' ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-violet-700'
            }`}
          >
            Create account
          </button>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="username" className={labelClass}>Username *</label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              required
              className={fieldClass}
              placeholder="Choose a username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="password" className={labelClass}>Password *</label>
            <PasswordInput
              id="password"
              autoComplete={authMode === 'register' ? 'new-password' : 'current-password'}
              required
              className={fieldClass}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {authMode === 'register' && (
            <>
              <div>
                <label htmlFor="name" className={labelClass}>Name (optional)</label>
                <input
                  id="name"
                  type="text"
                  className={fieldClass}
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="school" className={labelClass}>School *</label>
                <select
                  id="school"
                  className={fieldClass}
                  value={school}
                  onChange={(e) => setSchool(e.target.value)}
                  disabled={!options}
                >
                  <option value="">Select your school</option>
                  {options?.schools.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <SubjectChipsSelect
                value={subjects}
                substitutes={substituteSubjects}
                onChange={(next, nextSubs) => {
                  setSubjects(next);
                  setSubstituteSubjects(nextSubs);
                }}
              />

              <div>
                <label className={labelClass}>Classes you teach *</label>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <select
                      aria-label="Grade"
                      className={fieldClass}
                      value={pendingGrade}
                      onChange={(e) => setPendingGrade(e.target.value)}
                      disabled={!options}
                    >
                      <option value="">Grade</option>
                      {options?.grades.map((g) => (
                        <option key={g} value={g}>Grade {g}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <select
                      aria-label="Section"
                      className={fieldClass}
                      value={pendingSection}
                      onChange={(e) => setPendingSection(e.target.value)}
                      disabled={!options}
                    >
                      <option value="">No section</option>
                      {options?.sections.map((s) => (
                        <option key={s} value={s}>Section {s}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={addClass}
                    className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 hover:bg-violet-200 sm:h-[42px] sm:w-[42px]"
                    aria-label="Add class"
                  >
                    <AddRounded />
                  </button>
                </div>

                {classes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {classes.map((c, idx) => (
                      <span
                        key={`${c.grade}-${c.section ?? ''}`}
                        className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 text-sm text-indigo-700"
                      >
                        {classLabel(c)}
                        <button
                          type="button"
                          onClick={() => removeClass(idx)}
                          className="text-indigo-400 hover:text-indigo-700"
                          aria-label={`Remove ${classLabel(c)}`}
                        >
                          <CloseRounded fontSize="small" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="mt-1 text-xs text-slate-500">Add each grade/section you want to see reports for.</p>
              </div>
            </>
          )}

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center text-sm text-red-600">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="flex w-full justify-center rounded-xl border border-transparent bg-indigo-500 px-4 py-3 text-base font-medium text-white hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:py-2 sm:text-sm"
          >
            {isLoading
              ? authMode === 'register' ? 'Creating account...' : 'Signing in...'
              : authMode === 'register' ? 'Create account' : 'Log in'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => navigate('/')}
          className="mx-auto flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-violet-700"
        >
          <ArrowBackRounded fontSize="small" /> Back to home
        </button>
      </div>
    </div>
  );
};

export default TeacherAuth;
